import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { STRIPE_QUOTE_CATALOG, STRIPE_QUOTE_HARDWARE_PRICE } from '@staff-app/graphql-client';
import { formatMoney } from '../pricing/cents';
import { hardwareRequest, type HardwarePriceLine } from '../pricing/us-hardware';
import type { QuoteRequestLine } from '../pricing/types';

interface CatalogHardware {
  sku: string;
  stripePriceId?: string | null;
  name: string;
  unitAmountMinor?: number | null;
  quotable: boolean;
}

/**
 * The Hardware (US only) section's catalog and prices, from the Stripe Quote Generator's
 * server side — the same products, Stripe-priced configurations and freight bands.
 */
export function useUsHardware(rows: QuoteRequestLine[], allowed: boolean) {
  const catalogQ = useQuery<{ stripeQuoteCatalog: { hardware: CatalogHardware[] } }>(
    STRIPE_QUOTE_CATALOG,
    { fetchPolicy: 'cache-and-network' },
  );
  const options = useMemo(
    () =>
      (catalogQ.data?.stripeQuoteCatalog.hardware ?? [])
        .filter((h) => h.quotable && h.unitAmountMinor != null)
        .map((h) => ({
          value: h.stripePriceId ? `${h.sku}|${h.stripePriceId}` : h.sku,
          label: `${h.name} — ${formatMoney(h.unitAmountMinor, 'usd')}`,
        })),
    [catalogQ.data],
  );

  const hardware = hardwareRequest(rows);
  const priceQ = useQuery<{ stripeQuoteHardwarePrice: { lines: HardwarePriceLine[] } }>(
    STRIPE_QUOTE_HARDWARE_PRICE,
    { variables: { hardware }, skip: !allowed || !hardware.length, fetchPolicy: 'cache-and-network' },
  );

  return {
    options,
    catalogError: catalogQ.error?.message ?? null,
    priced: priceQ.data?.stripeQuoteHardwarePrice.lines ?? null,
    priceError: priceQ.error?.message ?? null,
    loading: catalogQ.loading || priceQ.loading,
  };
}
